import { EntityTypes, TextInterface } from '@email-editor/ui'
import {
  convertDraftToTiptap,
  EditorExtensionEnum,
  emailTextExtensions,
  TipTapEditor,
} from '@systemeio/tip-tap'
import { CSSProperties, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { twJoin, twMerge } from 'tailwind-merge'
import { emailSelectors, managementSelectors, useEmail, useManagement } from '../../store'
import * as emailActions from '../../store/email/actions'
import { generateId } from '../../store/email/utils'
import BaseEntity from '../core/BaseEntity/BaseEntity'
import { useSubstitutions } from 'src/editor/hooks/use-substitutions'

type TextProps = {
  entity: TextInterface
  position: number
  parentId: string
}

export const IS_TIP_TAP_EDITING = 'isTipTapEditing'

const Text = ({ entity, position, parentId }: TextProps) => {
  const dispatch = useDispatch()
  const isEditing = useManagement(state => managementSelectors.isEditing(state, entity.id))
  const linkColor = useEmail(emailSelectors.getLinkColor)

  function updateHtml(html: string) {
    dispatch(
      emailActions.updateEntity({
        ...entity,
        html,
      }),
    )
  }

  const extensionOption = {
    [EditorExtensionEnum.Link]: {
      color: linkColor,
    },
    [EditorExtensionEnum.FontSize]: {
      clearable: true,
    },
  }

  const globalSettings = useEmail(emailSelectors.getSettings)

  const getStyle = () => {
    const style: CSSProperties = { wordBreak: 'break-word' }
    if (globalSettings.textFontSize) {
      style.fontSize = `${globalSettings.textFontSize}px`
    }
    if (entity.lineHeight) {
      style.lineHeight = entity.lineHeight
    }
    if (entity.padding) {
      style.padding = `${entity.padding.paddingTop}px ${entity.padding.paddingRight}px ${entity.padding.paddingBottom}px ${entity.padding.paddingLeft}px`
    }

    return style
  }

  const migrateToTipTap = (rawContentState?: string) => {
    const html = convertDraftToTiptap({
      draftContent: rawContentState ?? '',
      additionalStyles: {
        fontFamily: entity.fontFamily,
        textAlign: entity.textAlign,
      },
      extension: {
        list: emailTextExtensions,
        options: extensionOption,
      },
    })
    dispatch(
      emailActions.updateEntity({
        ...entity,
        html,
        migratedToTipTap: true,
      }),
    )
    return html
  }

  useEffect(() => {
    window[IS_TIP_TAP_EDITING] = isEditing
  }, [isEditing])

  const { substitutions } = useSubstitutions()

  return (
    <BaseEntity
      className={twJoin(isEditing && 'rounded-t-lg')}
      entity={entity}
      position={position}
      parentId={parentId}
    >
      <div className="font-inter text-text-tertiary">
        <TipTapEditor
          substitutions={substitutions}
          toolbarClassName={twMerge(
            'font-inter text-text-tertiary border-0',
            isEditing ? 'opacity-100' : 'opacity-0 -z-1 pointer-events-none h-0 p-0',
          )}
          showAllItems
          style={getStyle()}
          content={
            // TODO remove after migration
            entity.migratedToTipTap ? entity.html : migrateToTipTap(entity.rawContentState)
          }
          onChange={updateHtml}
          className={twJoin(
            isEditing ? 'z-1 relative' : 'pointer-events-none',
            'h-fit min-h-fit border-0',
          )}
          extensions={emailTextExtensions}
          extensionOption={extensionOption}
        />
      </div>
    </BaseEntity>
  )
}

export function createText(): TextInterface {
  return {
    id: generateId(),
    type: EntityTypes.Text,
    html: 'Lorem ipsum dolor sit amet, consectetur adipisicing elit. Autem dolore, alias, numquam enim ab voluptate id quam harum ducimus cupiditate similique quisquam et deserunt, recusandae.',
    migratedToTipTap: true,
    rawContentState:
      '{"entityMap":{},"blocks":[{"key":"7gjsu",' +
      '"text":"Lorem ipsum dolor sit amet, consectetur adipisicing elit. Autem dolore, alias, ' +
      'numquam enim ab voluptate id quam harum ducimus cupiditate similique quisquam et deserunt, ' +
      'recusandae.","type":"unstyled","depth":0,"inlineStyleRanges":[],"entityRanges":[],"data":{}}]}',
    padding: {
      paddingTop: 15,
      paddingRight: 15,
      paddingBottom: 15,
      paddingLeft: 15,
    },
    lineHeight: 1.3,
    // TODO remove after migration. Doesn't used actually
    textAlign: 'center',
  }
}

export default Text
